import { assignSafe, isString, SDate, ValidationMessage } from '@summit/core';
import { CellClassParams, RowClassParams } from 'ag-grid-enterprise';

import { IUserExperience } from '../services/i-user-experience';
import { DisplayType } from './display-type.enum';
import { FilterParameter } from './filter-parameter';
import { IDisplaySchema } from './i-display-schema';
import { ListFooter } from './list-footer';
import { SortAttribute } from './sort-attribute';
import { SummitAction } from './summit-action';
import { SummitConfirmConfigs } from './summit-confirm-configs';
import { SummitDisplay } from './summit-display';
import { SummitIdObject } from './summit-id-object';
import { ViewConfig } from './view/view-config';
import { ViewField } from './view/view-field';

export type ObjectSchema<T> = Partial<Record<keyof T & string, Partial<ViewField<T>>>>;
export type RowClassRules<T> = {
  [cssClassName: string]: (params: RowClassParams<T>) => boolean;
};

export abstract class DisplaySchema<T extends SummitIdObject> implements IDisplaySchema<T> {
  abstract refTitle: string;
  defaultView?: string;
  refServerObjectName?: string;
  refServerAPI?: string;
  abstract refColumnList: ObjectSchema<T>; // | Partial< ViewField<T>>[];
  confirmFunctions?: SummitConfirmConfigs;
  abstract refViewModels: ViewConfig<T>[];
  refFilterParameters: FilterParameter[] = [];
  rowClassRules?: RowClassRules<T>;
  clientOnlyFields?: string[];
  excelColumnFreeze?: number;

  abstract NewObject(jsonData?: Partial<T>, ux?: IUserExperience, schema?: IDisplaySchema<T>): T;
  constructor(defaultView?: string, overrides?: Partial<DisplaySchema<T>>) {
    this.defaultView = defaultView;
    if (overrides) {
      assignSafe(this, overrides);
    }
  }

  //Default Client Route to Server api
  get ClientRoute(): string | undefined {
    return this.refServerAPI?.toLowerCase();
  }
  public Clean(dirtyObject: T): Partial<T> {
    if (this.clientOnlyFields) {
      this.clientOnlyFields.forEach((item: string) => {
        this.CleanParameter(dirtyObject, item);
      });
    }
    return dirtyObject;
  }

  private CleanParameter<T>(dirtyParameter: T, propertyName: string): T {
    //console.log(`Cleaning ${propertyName}`);
    if (propertyName.indexOf('.') >= 0) {
      const propertyTree = propertyName.split('.');
      const currentPropertyName = propertyTree.shift() as keyof T;
      if (!currentPropertyName) return dirtyParameter;
      if (Array.isArray(dirtyParameter[currentPropertyName])) {
        (dirtyParameter[currentPropertyName] as Array<object>).forEach((dirtyArrayEntry) => {
          this.CleanParameter(dirtyArrayEntry, propertyTree.join('.'));
        });
      } else {
        this.CleanParameter(dirtyParameter[currentPropertyName], propertyTree.join('.'));
      }
    } else {
      //console.log(`deleting ${propertyName}`);
      try {
        delete dirtyParameter[propertyName as keyof T];
        // eslint-disable-next-line no-empty
      } catch {}
    }
    return dirtyParameter;
  }

  // Object =
  private FindAttribute(
    entries: ObjectSchema<T>,
    attributeKey: keyof T & string,
    branch?: string
  ): ViewField<T> | undefined {
    if (Array.isArray(entries)) {
      if (!branch) {
        const response = entries.find((entry) => entry.propertyName === attributeKey);
        if (response) {
          response.key = attributeKey;
        }
        return response;
      }
      const branches = branch.split('.');
      // Find Branch
      const currentNodeName = branches.shift();
      const currentNode = entries.find((entry) => entry.propertyName === currentNodeName);
      if (!currentNode) {
        console.error(`Could not find Branch ${branch} for Field ${attributeKey?.toString()} on tree`, this);
      }
      // start search on short branch
      return this.FindAttribute(currentNode.children, attributeKey, branches.join('.'));
    } else {
      if (!branch) {
        //console.log(`Pulling ${attributeKey.toString()} from`, entries);
        const response = isString(attributeKey) && entries ? entries[attributeKey.toString()] : null;
        if (response) {
          response['key'] = attributeKey;
        }
        return response as ViewField<T>;
      }
      const branches = branch.split('.');
      // Find Branch
      const currentNodeName = branches.shift();

      if (currentNodeName) {
        const currentNode = entries[currentNodeName];
        if (!currentNode || !currentNode['children']) {
          console.error(`Could not find Branch ${branch} for Field ${attributeKey?.toString()} on tree`, this);
          return undefined;
        }
        return this.FindAttribute(currentNode['children'], attributeKey, branches.join('.'));
      }
      // start search on short branch
    }
    return undefined;
  }

  public getAttribute(attributeKey: keyof T & string, branch?: string): ViewField<T> | undefined {
    return this.FindAttribute(this.refColumnList, attributeKey, branch);
  }

  public getDisplayName(attributeKey: keyof T & string, branch?: string): string | undefined {
    const property = this.getAttribute(attributeKey, branch);
    return property ? property.displayName : undefined;
  }
  getColumnAttributeList(order?: (keyof T)[]): Array<ViewField<T>> {
    let response: ViewField<T>[];
    if (Array.isArray(this.refColumnList)) {
      response = this.refColumnList
        .filter((entry) => entry?.columnField)
        .map((entry) => new ViewField(entry, entry.propertyName));
      //console.log('response', response);
    } else {
      response = Object.keys(this.refColumnList)
        .filter((key) => this.refColumnList[key]?.columnField)
        .map((key) => new ViewField(this.refColumnList[key], key as keyof T & string));
    }

    if (order) {
      for (let i = order.length - 1; i >= 0; i--) {
        const currentIndex = response.findIndex((entry) => entry?.key === order[i]);
        // console.log(`Moving to front ${order[i]} @ ${currentIndex} of ${response.map(entry => entry.propertyName).join(',')}`);
        const newFront = response.splice(currentIndex, 1);
        response.unshift(newFront[0]);
        // console.log(`Done ${response.map(entry => entry.propertyName).join(',')}`);
      }
    }
    // console.timeEnd('display-config.getListColumns');
    return response;
  }

  getFormAttributeList(order?: string[]): Array<ViewField<T>> {
    let response: ViewField<T>[];
    if (Array.isArray(this.refColumnList)) {
      response = this.refColumnList
        .filter((entry) => entry.formField || entry.children)
        .map((entry) => new ViewField(entry));
    } else {
      response = Object.keys(this.refColumnList)
        .filter((key) => this.refColumnList[key]?.formField || this.refColumnList[key]?.children)
        .map((key) => new ViewField(this.refColumnList[key]));
    }

    if (order) {
      for (let i = order.length - 1; i >= 0; i--) {
        const currentIndex = response.findIndex((entry) => entry.propertyName === order[i]);
        // console.log(`Moving to front ${order[i]} @ ${currentIndex} of ${response.map(entry => entry.propertyName).join(',')}`);
        const newFront = response.splice(currentIndex, 1);
        response.unshift(newFront[0]);
        // console.log(`Done ${response.map(entry => entry.propertyName).join(',')}`);
      }
    }
    // console.timeEnd('display-config.getListColumns');
    return response;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFieldLabel(value: any, attribute: ViewField<T>): string | number | undefined {
    const tempObject = {} as Partial<T>;
    //if(this.refTitle==='Settlement Log Metrics') console.log('getFieldLabel', value, attribute);
    if (!attribute) return value;
    if (attribute.propertyName) {
      tempObject[attribute.propertyName] = value;
    }
    return this.getTextLabel(tempObject, attribute);
  }

  getTextLabel(element: Partial<T>, attribute: Partial<ViewField<T>> | undefined): string | number | undefined {
    //console.log('getTextLabel', element, attribute);
    if (!element) return undefined;
    return SummitDisplay.getAutoTextLabel(element, attribute);
  }
  getTextLabelLong(element: Partial<T>, attribute: Partial<ViewField<T>> | undefined): string | number | undefined {
    if (!element) return undefined;
    return SummitDisplay.getAutoTextLabel(element, attribute);
  }

  //Phase out after getTdClassString is moved to an array
  public getGridCellClass(params: CellClassParams): string[] {
    let response: string[] = [];
    if (!params.data) return response;
    const attribute = this.getAttribute(params.colDef.field as keyof T & string);
    if (!attribute && params.colDef.field === 'menu') {
      response.push('pr-0');
      response.push('pl-0');
      return response;
    }
    if (params.colDef.field && !attribute) console.log('No attribute found for', params.colDef.field);
    //attribute may be null for menu
    const classes = this.getTdClassString(params.data, attribute);
    if (classes?.length > 0) response = classes.split(' ');
    if (attribute?.displayClass) response = response.concat(attribute.displayClass.split(' '));
    return response;
  }

  /*
  Deprecating the following methods as we move to Grid
  */
  public getTdClassString(_element: T, _attribute?: ViewField<T> | null, _overFlow?: boolean): string {
    return '';
  }
  public getFooterTextLabel(data: Array<T>, attribute: ViewField<T>): string | number | string[][] | undefined {
    return SummitDisplay.getFooterTextLabel(data, attribute, this);
  }
  public getGroupFooterTextLabel(
    data: ListFooter<T>,
    attribute: ViewField<T>
  ): string | number | string[][] | undefined {
    return SummitDisplay.getFooterTextLabel(data.elements, attribute, this);
  }
  getGroupFooterClass(_element: T, _attribute: ViewField<T>): string {
    return '';
  }
  public hideRow(_row: unknown): boolean {
    return false;
  }
  /*
  End Deprecation
  */

  public GetFilter(filterName: string): FilterParameter | undefined {
    return this.refFilterParameters.find((entry) => entry.id === filterName);
  }

  public SetFilter(filterName: string, state: boolean, data?: unknown) {
    //if (filterName == 'Active') console.log(`Setting Filter ${filterName} to ${state}`, this.refFilterParameters);
    const lclFilter = this.refFilterParameters.find((entry) => entry.id === filterName);
    //if (filterName == 'Active') console.log('Found Filter', lclFilter);
    if (!lclFilter) {
      if (state === true)
        console.error(
          `Could not find filter ${filterName} in Schema for ${this.refTitle} for object ${this.refServerObjectName}`
        );
      return;
    }
    lclFilter.state = state;
    if (data != null && data != undefined) {
      if (SDate.isDayjs(data)) {
        lclFilter.filterData = data.format('YYYY-MM-DD');
      } else {
        lclFilter.filterData = data;
      }
    }
    //if (filterName == 'Active') console.log('Set Filter', lclFilter);
  }

  public SetFilterJsonValue(filterName: string, state: boolean, data?: unknown) {
    const lclFilter = this.refFilterParameters.find((entry) => entry.id === filterName);
    if (!lclFilter) return;
    lclFilter.state = state;
    if (data != null && data != undefined) lclFilter.filterData = encodeURIComponent(JSON.stringify(data));
  }

  /*
    Section to modify the view based on UX changes.
    */

  public RenameAttribute(attributeName: keyof T & string, newName: string) {
    if (newName?.length > 0) {
      const renameAttribute = this.getAttribute(attributeName);
      if (renameAttribute) renameAttribute.displayName = newName;
      else console.warn('Could not find attribute', attributeName);
    }
  }
  public ChangeAttributeType(attributeName: keyof T & string, displayType: DisplayType) {
    const renameAttribute = this.getAttribute(attributeName);
    if (renameAttribute) renameAttribute.displayType = displayType;
    else console.warn('Could not find attribute', attributeName);
  }
  public SetDisplayHint(attributeName: keyof T & string, newValue: string) {
    if (newValue?.length > 0) {
      const attribute = this.getAttribute(attributeName);
      if (attribute) attribute.displayHint = newValue;
      else console.warn('Could not find attribute', attributeName);
    }
  }
  public DuplicateAttribute(attributeName: keyof T & string, newName: string, newDisplayName?: string) {
    if (newName?.length > 0) {
      const oldAttribute = this.getAttribute(attributeName);
      if (oldAttribute) {
        const newAttribute = new ViewField(oldAttribute);
        if (newDisplayName) newAttribute.displayName = newDisplayName;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.refColumnList as any)[newName] = newAttribute;
      } else console.warn('Could not find attribute', attributeName);
    }
  }
  public SetWidthMin(
    attributeName: keyof T & string,
    newMinWidth: number,
    newWidth?: number,
    newMaxWidth?: number,
    newFlex?: number
  ) {
    const attribute = this.getAttribute(attributeName);
    if (attribute) {
      if (newMinWidth != undefined) attribute.widthMin = newMinWidth;
      if (newWidth != undefined) attribute.width = newWidth;
      if (newMaxWidth != undefined) attribute.widthMax = newMaxWidth;
      if (newFlex != undefined) attribute.widthFlex = newFlex;
    } else console.warn('Could not find attribute', attributeName);
  }
  public SetLink(attributeName: keyof T & string, routerLink: string[], queryParams?: string[][]) {
    const renameAttribute = this.getAttribute(attributeName);
    if (renameAttribute) {
      renameAttribute.routerLink = routerLink;
      renameAttribute.queryParams = queryParams;
    } else console.warn('Could not find attribute', attributeName);
  }
  public ChangeLink(attributeName: keyof T & string, newValue: string[]) {
    const renameAttribute = this.getAttribute(attributeName);
    if (renameAttribute) renameAttribute.routerLink = newValue;
    else console.warn('Could not find attribute', attributeName);
  }

  public SetAction(attributeName: keyof T & string, action: SummitAction) {
    const renameAttribute = this.getAttribute(attributeName);
    if (renameAttribute) {
      renameAttribute.action = action;
    } else console.warn('Could not find attribute', attributeName);
  }

  public DeleteAttribute(attributeName: string) {
    if (Array.isArray(this.refColumnList)) {
      this.refColumnList.splice(
        this.refColumnList.findIndex((entry) => entry.propertyName == attributeName),
        1
      );
    } else {
      delete this.refColumnList[attributeName];
    }
  }

  public GetView(viewName?: string) {
    const lclViewName = viewName ?? this.defaultView;
    return this.refViewModels.find((entry) => entry.id == lclViewName);
  }
  public AddView(view: ViewConfig<T>) {
    if (!this.refViewModels.find((entry) => entry.id == view.id)) {
      this.refViewModels.push(view);
    }
  }
  public RenameView(viewName: string, newName: string) {
    const view = this.refViewModels.find((entry) => entry.id == viewName);
    if (view) {
      view.name = newName;
    } else {
      console.error(`Could not find view ${viewName} to rename`);
    }
  }

  public HideView(viewName: string) {
    this.refViewModels.splice(
      this.refViewModels.findIndex((entry) => entry.id == viewName),
      1
    );
  }
  protected PrependColumns(viewName: string, colNames: (keyof T & string)[]) {
    const view = this.refViewModels.find((item) => item.id == viewName);
    if (!view || !view.viewColumns) return;
    for (let i = colNames.length - 1; i >= 0; i--) {
      view.viewColumns.unshift(colNames[i]);
    }
  }
  public AddColumns(viewName: string, colNames: (keyof T & string)[]) {
    const view = this.refViewModels.find((item) => item.id == viewName);
    if (!view || !view.viewColumns) return;
    const viewColumns = view.viewColumns;
    colNames.forEach((colName) => {
      if (!viewColumns.includes(colName)) {
        viewColumns.push(colName);
      }
    });
  }
  public HideColumns(viewName: string, colNames: string[]) {
    const view = this.refViewModels.find((item) => item.id == viewName);
    if (!view || !view.viewColumns) return;
    const viewColumns = view.viewColumns;
    colNames.forEach((colName) => {
      //console.log(`Removing ${colName} from ${view.id}`);
      const index = viewColumns.findIndex((entry) => entry == colName);
      if (index >= 0) viewColumns.splice(index, 1);
    });
  }
  getViewFilterParams(viewName?: string): FilterParameter[] {
    viewName ??= this.defaultView;
    const updatedRefViewModels = this.refViewModels.find((item) => item.id === viewName);
    if (!updatedRefViewModels) return [];
    const newFilterParameters: FilterParameter[] =
      updatedRefViewModels.filterParameters?.map((entry) => new FilterParameter(entry)) ?? [];

    return newFilterParameters;
  }
  protected SortBy(viewName: string, colName: string) {
    const view = this.refViewModels.find((item) => item.id == viewName);
    if (!view) return;
    if (Array.isArray(view.sortParameters)) {
      if (view.sortParameters.length > 0) {
        view.sortParameters[0]['id'] = colName;
        view.sortParameters.length = 1;
      }
    } else {
      view.sortParameters ??= new SortAttribute();
      view.sortParameters['id'] = colName;
    }
  }

  public AddValidation(attributeName: keyof T & string, message: ValidationMessage) {
    const attribute = this.getAttribute(attributeName);
    if (attribute) {
      attribute.validationMessages ??= [];
      attribute.validationMessages.push(message);
    } else console.warn('Could not find attribute', attributeName);
  }

  public ChangeValidation(attributeName: keyof T & string, oldKey: string, newMessage: ValidationMessage) {
    const attribute = this.getAttribute(attributeName);
    if (attribute) {
      const message = attribute.validationMessages?.find((entry) => entry.type == oldKey);
      if (message) {
        if (message.type) message.type = newMessage.type;
        if (message.message) message.message = newMessage.message;
      }
    } else console.warn('Could not find attribute', attributeName);
  }

  public ChangeHint(attributeName: keyof T & string, newValue: string) {
    const renameAttribute = this.getAttribute(attributeName);
    if (renameAttribute) renameAttribute.displayHint = newValue;
    else console.warn('Could not find attribute', attributeName);
  }
}
